import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "@mmfestival/shared-components";
import { Country, State, City } from "country-state-city";

export const InputFieldProfile = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
}) => {
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [countryMap, setCountryMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [errors, setErrors] = useState({});

  // Initialize countries on mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const countryList = allCountries.map((country) => ({
      value: country.name,
      label: country.name,
      isoCode: country.isoCode,
    }));
    setCountries(countryList);
    
    // Create a map for quick lookup
    const cMap = {};
    allCountries.forEach((country) => {
      cMap[country.name] = country.isoCode;
    });
    setCountryMap(cMap);
  }, []);

  // Load provinces when country changes
  useEffect(() => {
    if (formData.negara && countryMap[formData.negara]) {
      setProvinces([]);
      setCities([]);
      
      const countryCode = countryMap[formData.negara];
      const allStates = State.getStatesOfCountry(countryCode);
      
      const provinceList = allStates.map((state) => ({
        value: state.name,
        label: state.name,
        isoCode: state.isoCode,
      }));
      setProvinces(provinceList);
      
      // Create state map for quick lookup
      const sMap = {};
      allStates.forEach((state) => {
        sMap[state.name] = state.isoCode;
      });
      setStateMap(sMap);
    }
  }, [formData.negara, countryMap]);

  // Load cities when province changes
  useEffect(() => {
    if (formData.provinsi && formData.negara && countryMap[formData.negara] && stateMap[formData.provinsi]) {
      setCities([]);
      
      const countryCode = countryMap[formData.negara];
      const stateCode = stateMap[formData.provinsi];
      const allCities = City.getCitiesOfState(countryCode, stateCode);
      
      const cityList = allCities.map((city) => ({
        value: city.name,
        label: city.name,
      }));
      setCities(cityList);
    }
  }, [formData.provinsi, formData.negara, countryMap, stateMap]);

  const fieldLabels = {
    namaDepan: "First name",
    namaBelakang: "Last name",
    telepon: "Phone / WhatsApp",
    tanggalLahir: "Date of birth",
    gender: "Gender",
    negara: "Country",
    provinsi: "Province",
    kota: "City",
    alamat: "Address",
    email: "Email",
  };

  const validateField = (name, value) => {
    if (!value && formData[name]?.required) {
      const label = fieldLabels[name] || name;
      return `${label} is required`;
    }
    return "";
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };

    if (name === "negara") {
      updatedFormData.provinsi = "";
      updatedFormData.kota = "";
    } else if (name === "provinsi") {
      updatedFormData.kota = "";
    }

    handleChange({ target: { name, value } });

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}
      <InputField
        label="First name"
        name="namaDepan"
        value={formData.namaDepan || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.namaDepan}
        labelColor={labelColor}
      />
      <InputField
        label="Last name"
        name="namaBelakang"
        value={formData.namaBelakang || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.namaBelakang}
        labelColor={labelColor}
      />

      <InputField
        label="Email"
        name="email"
        type="email"
        required
        value={formData.email}
        onChange={handleChange}
        disabled={true}
        labelColor={labelColor}
      />

      <InputField
        label="Phone / WhatsApp"
        name="telepon"
        value={formData.telepon || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.telepon}
        labelColor={labelColor}
      />

      <InputField
        label="Date of birth"
        name="tanggalLahir"
        type="date"
        value={formData.tanggalLahir || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.tanggalLahir}
        labelColor={labelColor}
      />

      <InputField
        label="Gender"
        name="gender"
        type="select"
        value={formData.gender || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        options={[
          { value: "", label: "Select gender" },
          { value: "Male", label: "Male" },
          { value: "Female", label: "Female" },
        ]}
        error={errors.gender}
        labelColor={labelColor}
      />
      <InputField
        label="Country"
        name="negara"
        type="select"
        value={formData.negara || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        options={[{ value: "", label: "Select country" }, ...countries]}
        error={errors.negara}
        labelColor={labelColor}
      />
      <InputField
        label="Province"
        name="provinsi"
        type="select"
        value={formData.provinsi || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.negara}
        options={[{ value: "", label: "Select province" }, ...provinces]}
        error={errors.provinsi}
        labelColor={labelColor}
      />
      <InputField
        label="City"
        name="kota"
        type="select"
        value={formData.kota || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.provinsi}
        options={[{ value: "", label: "Select city" }, ...cities]}
        error={errors.kota}
        labelColor={labelColor}
      />
      <InputField
        label="Address"
        name="alamat"
        value={formData.alamat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.alamat}
        labelColor={labelColor}
      />
    </div>
  );
};
